import React, { useState, useEffect, useRef } from "react";
import { Grid, Modal, Hidden, Box, Chip } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { FormProvider, useForm } from "react-hook-form";
import DnerhsApi from "api/DnerhsApi";
import { trackPromise } from 'react-promise-tracker';
import FormInput from "../../Controls/Input";
import GetAppIcon from "@material-ui/icons/GetApp";
import commonMethods from "../../utils/commonMethods";
import Header from "components/Header";
import AlertMUI from '@material-ui/lab/Alert';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Button
} from "@material-ui/core";
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import IconButton from "@material-ui/core/IconButton";
import ClearIcon from '@material-ui/icons/Clear';
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import { ReporteApi } from "config/ApiUrls";
import Alert from "components/Alert";
import history from "../../utils/History";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { Link as RouterLink,  useParams ,useLocation } from "react-router-dom";
//import DescargarCertificadoVer from "layout/DescargarCertificado/FormVerCertificado";
import pie from "../../images/pie.png";
import encabezado from "../../images/encabezado_new_2023.jpg";
import Fab from "@material-ui/core/Fab";
import "react-toastify/dist/ReactToastify.css";

const useStyles = makeStyles((theme) => ({
  main: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4),
      paddingTop: theme.spacing(3),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(8),
      paddingTop: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(16),
      paddingTop: theme.spacing(3),
    },
  },
  form: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  field: {
    margin: theme.spacing(),
  },
  button: {
    margin: theme.spacing(),
    paddingTop: theme.spacing(2),
  },
  alert: {
    color: "#ff0000",
  },
  modal: {
    position:'absolute',
    width: '90%',
    maxWidth: 1200,
    backgroundColor: 'white',
    borderRadius: 5,
    borderColor: 'white',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2,4,2),
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    focus:{
      outline: 'none'
    },
    outline: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '95%',
      padding: theme.spacing(2),
    },
  },
  card: {
    margin: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing(4),
    },
    [theme.breakpoints.up('md')]: {
      marginLeft: theme.spacing(10),
      marginRight: theme.spacing(10),
    },
  },
  contentPadding: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4),
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
    },
  },
  imagePadding: {
    padding: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: '10%',
      paddingRight: '10%',
    },
  },
  tableContainer: {
    overflowX: 'auto',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  responsiveImage: {
    width: '100%',
    height: 'auto',
    maxWidth: '447px',
  },
  // Estilos para cards en móvil
  mobileCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  mobileCardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  mobileCardLabel: {
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  mobileCardValue: {
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    textAlign: 'right',
    maxWidth: '60%',
  },
  // Botón flotante de descarga
  fabButton: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1000,
    [theme.breakpoints.up('md')]: {
      display: 'none', // Ocultar en desktop
    },
  },
  // Tipografía responsive
  responsiveTitle: {
    fontSize: '1.25rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.5rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1.75rem',
    },
  },
  responsiveBody: {
    fontSize: '0.875rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1.25rem',
    },
  },
  // Estilos de impresión
  '@media print': {
    main: {
      padding: '0 !important',
    },
    card: {
      margin: '0 !important',
      boxShadow: 'none !important',
    },
    contentPadding: {
      padding: '10px !important',
    },
    fabButton: {
      display: 'none !important',
    },
    tableContainer: {
      overflowX: 'visible !important',
    },
  },
}));

const validationSchema = yup.object().shape({
  ci: yup.string().required("Es un campo requerido")
});

const Form = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const methods = useForm({
    resolver: yupResolver(validationSchema)
  });
  const { handleSubmit, errors, formState, reset } = methods;
  const [constancias, setConstancias] = useState([]);
  const [constanciaDetalles, setConstanciaDetalles] = useState([]);

  const [alumno, setAlumno] = useState("");
  const [cedula, setCedula] = useState("");
  const [cedulaExtranjero, setCedulaExtranjero] = useState("");
  const [formadora, setFormadora] = useState("");
  const [sede, setSede] = useState("");
  const [nacionalidad, setNacionalidad] = useState("");
  const [hoy, setHoy] = useState("");
  const [mesActual, setMesActual] = useState("");
  const [anhoActual, setAnhoActual] = useState("");
  const { documento } = useParams();
  const params = useParams();
  const [warningMessage, setWarningMessage] = useState();
  const [constanciaId, setConstanciaId] = useState(null);
  useEffect(() => {

    const load = () => {
      try {
        getServerData();
      } catch (error) {
        console.log(error);
      }
    };
    load();
  }, []);


  const getServerData = async () => {
    setWarningMessage();
    try {
      //let url = `/constancias/estudiante?cedula=${params.documento}`;
      let url = `/constancias/listado-practicas-cedula?cedula=${params.documento}`;

      const { data } = await DnerhsApi(url, {

      });
        if (data && data.length > 0) {

          setConstancias(data);
          setAlumno(data[0].practicaDetalle.convenioEstudiante.estudiante.nombres +" " + data[0].practicaDetalle.convenioEstudiante.estudiante.apellidos);
          setNacionalidad(data[0].practicaDetalle.convenioEstudiante.estudiante.nacionalidad.nombre);
          setCedula(data[0].practicaDetalle.convenioEstudiante.estudiante.cedulaIdentidad);
          setCedulaExtranjero(data[0].practicaDetalle.convenioEstudiante.estudiante.documentoExtranjero);
          setFormadora(data[0].practicaDetalle.convenioEstudiante.convenio.institucionFormadora.institucion);
          setSede(data[0].practicaDetalle.convenioEstudiante.convenio.institucionFormadora.sede);

          // Guardar el ID de la primera constancia para el botón flotante
          if (data[0].constanciaId || data[0].id) {
            setConstanciaId(data[0].constanciaId || data[0].id);
          }

          //setConstanciaDetalles(data[0].constanciaDetalleList);
          const fecha = new Date();
          setHoy(fecha.getDate());

          const monthNameLong = fecha.toLocaleString("es-AR", { month: "long" });

          setMesActual(monthNameLong);
          setAnhoActual(fecha.getFullYear());

        } else {
          setConstancias([]);
          setWarningMessage("¡No se encontraron datos para la búsqueda!")
        }


    } catch (error) {
      console.log(error);
      if (error.response) {
        Alert.showServerError(error);
      }
    }


  };
  const downloadFile = async (item) => {
    try {
      let name = "constancia"
      let constanciaIdParam = item?.constanciaId || item?.id;
      let url = `${ReporteApi.baseURL}?name=constancia&constanciaId=${constanciaIdParam}`;
      trackPromise(commonMethods.downloadFile(url, `${name}_${new Date()}.pdf`, true));
    } catch (error) {
      console.log(error);
      if (error.response) {
        Alert.showServerError(error);
      }
    }
  };

  // Función para el botón flotante
  const handleFloatingDownload = () => {
    if (constanciaId) {
      downloadFile({ id: constanciaId });
    }
  };

 /* const handleFormKeyPress = (event) => {
      if (event.key == "Enter") {
        event.preventDefault();
        handleSubmit(onSubmit)();
      }
  }*/
  
/*ayrray1.map((opcion) => {
  options.push(
    <option key={opcion.id} value={opcion.id}>{opcion.numero}</option>
  );
  opcion.array2.map((elem) => (
    options.push(
      <option key={elem.id} value={elem.id}>{elem.numero}</option>
    )
  ))
});

 */

  const resetSearch = () => {
    reset();
    setConstancias([]);
  }

  // Renderizar práctica como card (móvil)
  const renderPracticaCard = (item, key) => (
    <Card key={key} className={classes.mobileCard}>
      <Box className={classes.mobileCardRow}>
        <Typography className={classes.mobileCardLabel}>Institución:</Typography>
        <Typography className={classes.mobileCardValue}>
          {item.practicaDetalle.convenioEstudiante.convenio.institucionFormadora.institucion}
        </Typography>
      </Box>
      <Box className={classes.mobileCardRow}>
        <Typography className={classes.mobileCardLabel}>Sede:</Typography>
        <Typography className={classes.mobileCardValue}>
          {item.practicaDetalle.convenioEstudiante.convenio.institucionFormadora.sede}
        </Typography>
      </Box>
      <Box className={classes.mobileCardRow}>
        <Typography className={classes.mobileCardLabel}>Curso:</Typography>
        <Typography className={classes.mobileCardValue}>{item.curso}</Typography>
      </Box>
      <Box className={classes.mobileCardRow}>
        <Typography className={classes.mobileCardLabel}>Semestre:</Typography>
        <Typography className={classes.mobileCardValue}>{item.semestre}</Typography>
      </Box>
      <Box className={classes.mobileCardRow}>
        <Typography className={classes.mobileCardLabel}>Año:</Typography>
        <Typography className={classes.mobileCardValue}>{item.practicaDetalle.anio}</Typography>
      </Box>
      <Box className={classes.mobileCardRow}>
        <Typography className={classes.mobileCardLabel}>Materia:</Typography>
        <Typography className={classes.mobileCardValue}>{item.materia}</Typography>
      </Box>
      <Box className={classes.mobileCardRow}>
        <Typography className={classes.mobileCardLabel}>Carrera:</Typography>
        <Typography className={classes.mobileCardValue}>
          {item.convenioCarrera.carreraPrograma.descripcion === "Postgrado"
            ? "Postgrado - " + item.convenioCarrera.nombrePostgrado.descripcion
            : item.convenioCarrera.carreraPrograma.descripcion}
        </Typography>
      </Box>
      <Box className={classes.mobileCardRow}>
        <Typography className={classes.mobileCardLabel}>Tutor:</Typography>
        <Typography className={classes.mobileCardValue}>
          {item.practicaDetalle.convenioTutor.tutor.nombres + " " + item.practicaDetalle.convenioTutor.tutor.apellidos}
        </Typography>
      </Box>
      <Box className={classes.mobileCardRow}>
        <Typography className={classes.mobileCardLabel}>Establecimiento:</Typography>
        <Typography className={classes.mobileCardValue}>
          {item.practicaDetalle.establecimiento.nombreServicio.nombre}
        </Typography>
      </Box>
      <Box className={classes.mobileCardRow}>
        <Typography className={classes.mobileCardLabel}>Total Horas:</Typography>
        <Chip
          label={item.practicaDetalle.totalHoras}
          color="primary"
          size="small"
        />
      </Box>
    </Card>
  );

  //, de la Institución Formadora   <strong>{formadora}</strong> , Sede  <strong>{sede}</strong>

  return (
    <>
          <Header />

      <br/> <br/> <br/>
         <Grid container direction="row" justifyContent="center" alignItems="center">
            <Typography align="center" variant="h6" color="primary" className={classes.responsiveTitle} style={{ margin: "10px" }} >
              Certificado de Prácticas
            </Typography>
            <br/>
          </Grid>
          

    <Card className={classes.card}>
      <CardContent>
      <div align="center" className={classes.imagePadding}>
        <img alt="Encabezado" src={encabezado} style={{ width: '100%', height: 'auto' }} />
      </div>
      <Grid container direction="row" justifyContent="center" alignItems="center">
            <Typography align="center" variant="h6" color="primary" className={classes.responsiveTitle} style={{ margin: "10px" }} >
            CONSTANCIA DE PRÁCTICAS SUPERVISADAS
            </Typography>

          </Grid>

            <div className={classes.contentPadding}>
            <Typography align="center" variant="h6" className={classes.responsiveBody}>
            <em>
            El Ministerio de Salud P&uacute;blica y Bienestar Social a trav&eacute;s de la Direcci&oacute;n Nacional Estrat&eacute;gica de Recursos Humanos en Salud, hace Constar:
            </em>
            </Typography>
            </div>

            <div className={classes.contentPadding}>
            <Typography align="justify" variant="h6" className={classes.responsiveBody}>
            Que el Alumno/a  <strong>{alumno}</strong> de Nacionalidad <strong>{nacionalidad}</strong>
            {cedulaExtranjero===false? (" con Cédula de Identidad Civil N°"):( " con Documento de Identidad N°")}
           <strong>{cedula}</strong>  obtuvo la habilitación para la realización de las prácticas supervisadas en las dependencias del Ministerio de Salud Pública y Bienestar Social.
            </Typography>
            </div>
            <br/>
            <Grid item xs={12} className={classes.contentPadding}>

            {/* Vista de tabla para desktop */}
            <Hidden smDown>
              <div className={classes.tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Institucion Formadora</TableCell>
                      <TableCell align="center">Sede</TableCell>
                      <TableCell align="center">Curso </TableCell>
                      <TableCell align="center">Semestre </TableCell>
                      <TableCell align="center">Año</TableCell>
                      <TableCell align="center">Materia</TableCell>
                      <TableCell align="center">Carrera</TableCell>
                      <TableCell align="center">Tutor</TableCell>
                      <TableCell align="center">Establecimiento</TableCell>
                      <TableCell align="center">Total Horas</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {constancias.length > 0 ?
                      constancias.map((item, key) => (
                        <TableRow key={ key }>
                          <TableCell align="center">{item.practicaDetalle.convenioEstudiante.convenio.institucionFormadora.institucion}</TableCell>
                          <TableCell align="center">{item.practicaDetalle.convenioEstudiante.convenio.institucionFormadora.sede}</TableCell>
                          <TableCell align="left">{item.curso}</TableCell>
                          <TableCell align="left">{item.semestre}</TableCell>
                          <TableCell align="left">{item.practicaDetalle.anio}</TableCell>
                          <TableCell align="left">{item.materia}</TableCell>
                          <TableCell align="center">
                            {(item.convenioCarrera.carreraPrograma.descripcion==="Postgrado"? "Postgrado - " + item.convenioCarrera.nombrePostgrado.descripcion : item.convenioCarrera.carreraPrograma.descripcion )}
                          </TableCell>
                          <TableCell align="center">{item.practicaDetalle.convenioTutor.tutor.nombres  + " " +item.practicaDetalle.convenioTutor.tutor.apellidos }</TableCell>
                          <TableCell align="center">{item.practicaDetalle.establecimiento.nombreServicio.nombre}</TableCell>
                          <TableCell align="center">{item.practicaDetalle.totalHoras}</TableCell>
                        </TableRow>
                      ))
                      :
                      <TableRow>
                        <TableCell colSpan={12}>
                          <Typography align="center" color="primary" variant="body1">No hay datos para mostrar</Typography>
                        </TableCell>
                      </TableRow>
                    }
                  </TableBody>
                </Table>
              </div>
            </Hidden>

            {/* Vista de cards para móvil */}
            <Hidden mdUp>
              {constancias.length > 0 ? (
                constancias.map((item, key) => renderPracticaCard(item, key))
              ) : (
                <Card className={classes.mobileCard}>
                  <Typography align="center" color="primary" variant="body1">
                    No hay datos para mostrar
                  </Typography>
                </Card>
              )}
            </Hidden>

            <br/>
          </Grid>
          <br/>
            <div className={classes.contentPadding}>
            <Typography align="justify" variant="h6" className={classes.responsiveBody}>
            Se emite la presente constancia conforme a los datos consignados bajo declaración jurada por la citada Institución Formadora, responsable del cumplimiento de las exigencias académicas, la cual ha calificado y acreditado que el estudiante ha dado cumplimiento a los requisitos académicos respectivos.           </Typography>
            </div>
            <div className={classes.contentPadding}>
            <Typography align="justify" variant="h6" className={classes.responsiveBody}>
            La presente constancia tendrá validez en el marco de las gestiones contempladas en la Resolución del Ministerio de Educación y Ciencias N° 573/2021, ANEXO I Apartado 1.7 de fecha 31 de agosto de 2021
            </Typography>
            </div>
            <br/> <br/>
            <div className={classes.contentPadding}>
              <Typography align="right" variant="h6" className={classes.responsiveBody}>
                <strong> {hoy} de {mesActual} de {anhoActual}.- Paraguay.</strong>
              </Typography>
            </div>

            <Grid container spacing={2} className={classes.contentPadding}>
              <Grid item xs={12} sm={6}>
                <img alt="Pie" src={pie} className={classes.responsiveImage} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography align="right" variant="body1">
                  <strong>Avda. Tte. Cnel Mauricio Escobar c/ Venezuela </strong>
                  <br/>
                  <strong>Tel&eacute;fono: 595-21-282-815 Asunci&oacute;n - Paraguay</strong>
                  <br/>
                  <strong> www.dnerhs.gov.py </strong>
                </Typography>
              </Grid>
            </Grid>
            
      </CardContent>     
   </Card>
    <Grid
      container
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
    >
      <Grid item className={classes.button}>
        <Button
          variant="contained"
          color="default"
          onClick={() => history.goBack()}
        >
          Atrás
        </Button>
      </Grid>
    </Grid>

    {/* Botón flotante de descarga (solo móvil) */}
    {isMobile && constanciaId && (
      <Fab
        color="primary"
        aria-label="descargar"
        className={classes.fabButton}
        onClick={handleFloatingDownload}
      >
        <GetAppIcon />
      </Fab>
    )}
    </>
  );
};

export default Form;
